import { useEffect, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TripContent } from '../../services/api';
import MapPanel from './MapPanel';
import './MapPanelFloating.css';

interface MapPanelFloatingProps {
    isOpen: boolean;
    onClose: () => void;
    content: TripContent;
    selectedDay: { cityIdx: number; dayIdx: number };
    currentDayNumber: number;
    selectedActivityId: string | null;
    onClearSelectedActivity: () => void;
    /** Optional: title to show when focusing an activity */
    focusedActivityTitle?: string;
}

/**
 * MapPanelFloating - Floating overlay panel for map on desktop
 * Position: fixed, floats over itinerary column (never over chat)
 * Opens on "Show map" or "View on map" actions
 */
export default function MapPanelFloating({
    isOpen,
    onClose,
    content,
    selectedDay,
    currentDayNumber,
    selectedActivityId,
    onClearSelectedActivity,
    focusedActivityTitle,
}: MapPanelFloatingProps) {
    const { t } = useTranslation();
    const panelRef = useRef<HTMLDivElement>(null);
    const [hasBeenOpened, setHasBeenOpened] = useState(false);

    // Mount map only after first open (avoids Leaflet init until user opens panel)
    useEffect(() => {
        if (isOpen && !hasBeenOpened) {
            setHasBeenOpened(true);
        }
    }, [isOpen, hasBeenOpened]);

    // Handle ESC key to close
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape' && isOpen) {
            onClose();
        }
    }, [isOpen, onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // Handle click outside (only on itinerary area, not chat)
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            
            // Don't close if clicking inside the panel
            if (panelRef.current?.contains(target)) return;
            
            // Don't close if clicking on chat panel
            if (target.closest('.chat-sidebar') || target.closest('.chat-panel')) return;
            
            // Don't close if clicking on header actions (share, export, etc.)
            if (target.closest('.trip-header')) return;
            
            // Close if clicking anywhere else (itinerary area)
            onClose();
        };

        // Small delay to prevent immediate close on open click
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    // Don't render until first opened (defers Leaflet init)
    if (!hasBeenOpened) return null;

    // Panel title
    const panelTitle = focusedActivityTitle 
        ? focusedActivityTitle
        : t('trip.map_view', 'Map');

    return (
        <div 
            className={`map-floating-panel ${isOpen ? 'open' : 'closed'}`}
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={panelTitle}
            aria-hidden={!isOpen}
        >
            {/* Header */}
            <div className="map-floating-header">
                <div className="map-floating-title">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                        <line x1="8" y1="2" x2="8" y2="18" />
                        <line x1="16" y1="6" x2="16" y2="22" />
                    </svg>
                    <span className="title-text">{panelTitle}</span>
                </div>
                <div className="map-floating-actions">
                    {selectedActivityId && (
                        <button 
                            className="map-floating-clear-btn"
                            onClick={onClearSelectedActivity}
                            title={t('trip.clear_selection', 'Clear selection')}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="15" y1="9" x2="9" y2="15" />
                                <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                            <span>{t('trip.clear', 'Clear')}</span>
                        </button>
                    )}
                    <button 
                        className="map-floating-close-btn"
                        onClick={onClose}
                        aria-label={t('trip.hide_map', 'Hide map')}
                        title={t('trip.hide_map', 'Hide map')}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Map Content - static import so prod matches dev (no lazy/Suspense) */}
            <div className="map-floating-content">
                {isOpen && (
                    <MapPanel
                        content={content}
                        selectedDay={selectedDay}
                        currentDayNumber={currentDayNumber}
                        selectedActivityId={selectedActivityId}
                    />
                )}
            </div>
        </div>
    );
}
